// Performance cycles (Phase 4) — HR opens/closes appraisal periods that
// objectives belong to.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Trash2, CalendarRange } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';

const TYPES = ['Annual', 'Half-year', 'Quarterly', 'Custom'];

export default function PerformanceCycles({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.performanceCycles().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || busy) return; setBusy(true);
    try { await api.createPerformanceCycle(form); showToast?.('Cycle created'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const toggle = async (c) => { try { await api.updatePerformanceCycle(c.id, { status: c.status === 'Open' ? 'Closed' : 'Open' }); load(); } catch (e) { showToast?.(e.message); } };
  const remove = async (c) => { try { await api.deletePerformanceCycle(c.id); load(); } catch (e) { showToast?.(e.message); } };

  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Performance Cycles" sub="Open and close appraisal periods" />
        <PrimaryBtn onClick={() => setForm({ name: '', type: 'Half-year', startDate: '', endDate: '', companyWide: true })}><Plus size={14} style={{ marginRight: 6 }} /> New cycle</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={CalendarRange} title="No cycles" desc="Create a performance cycle, then assign objectives to it." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((c) => (
            <Card key={c.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{c.name} <Pill tone={c.status === 'Open' ? 'success' : 'neutral'}>{c.status}</Pill></div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{c.type}{c.startDate ? ` · ${c.startDate}` : ''}{c.endDate ? ` → ${c.endDate}` : ''}{c.entityId ? '' : ' · company-wide'} · {c.okrCount} objective(s)</div>
                </div>
                <div className="flex items-center gap-1">
                  <OutlineBtn onClick={() => toggle(c)}>{c.status === 'Open' ? 'Close' : 'Reopen'}</OutlineBtn>
                  <button onClick={() => remove(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New performance cycle</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Name" placeholder="H1 2026" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mb: 1.5 }} />
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</div>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <div className="grid grid-cols-2 gap-2">
                <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </div>
              <FormControlLabel control={<Checkbox checked={!!form.companyWide} onChange={(e) => setForm({ ...form, companyWide: e.target.checked })} />} label="Company-wide" />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={create} disabled={busy}>Create</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
